import { SearchOutlined } from "@ant-design/icons";
import { Button, Empty, Input, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { debounce, DebounceCounter } from "src/common/debounce";
import { onError } from "src/common/onError";
import { HighlightedText } from "src/components/HighlightedText";
import { activateByPath } from "src/components/MForm/formNav/activateByPath";
import { str2MfPath } from "src/components/MForm/MFormTypes";

import { MfExternalErrorIcon } from "../../MfExternalErrorIcon";
import { MfErrorRow } from "../MfErrorRow";
import { MfErrorsListStore } from "../MfErrorsListStore";
import * as styles from "./MfErrorsListExpanded.module.less";

type PropsMfErrorsListTable = {
  store: MfErrorsListStore;
};

export const MfErrorsListTable: React.FC<PropsMfErrorsListTable> = observer(
  ({ store }) => {
    const { height, filter } = store;

    const refBox = React.useRef<HTMLDivElement>(null);
    const [tblHeight, setTblHeight] = React.useState<number | undefined>();
    const [searchText, setSearchText] = React.useState(filter);
    const changeSearchText = (value: string) => {
      setSearchText(value);
      debounce(searchCounter, 300, () => {
        store.setFilter(value);
      });
    };
    React.useEffect(() => {
      const { current } = refBox;
      if (current) {
        let h = current.clientHeight;
        const thead = current.querySelector("thead");
        if (thead) h -= thead.offsetHeight;
        setTblHeight(h);
      }
    }, [height]);

    const gotoField = (path: string) => {
      setTimeout(() => {
        activateByPath(store.form.root, str2MfPath(path)).catch(onError);
      }, 10);
    };

    const columns: ColumnsType<MfErrorRow> = [
      {
        key: "critical",
        render: (_, { warning }) => <MfExternalErrorIcon warning={warning} />,
        width: 40,
        align: "center",
      },
      {
        key: "type",
        title: "Тип",
        render: (_, { typeName }) => (
          <HighlightedText
            className={styles.typeName}
            text={typeName}
            selected={filter}
          />
        ),
        width: 160,
      },
      {
        key: "attr",
        title: "Атрибут",
        render: (_, { attrName, path, displayPath }) =>
          !!attrName &&
          (path ? (
            <Tooltip
              title={displayPath}
              placement="bottom"
              styles={{
                root: { maxWidth: "100%" },
              }}
            >
              <Button
                size="small"
                type="link"
                className={styles.attrName}
                onClick={() => gotoField(path)}
              >
                <HighlightedText text={attrName} selected={filter} />
              </Button>
            </Tooltip>
          ) : (
            <HighlightedText text={attrName} selected={filter} />
          )),
        width: 300,
      },
      {
        key: "message",
        title: "Сообщение",
        render: (_, { message }) => (
          <HighlightedText text={message} selected={filter} />
        ),
      },
    ];

    return (
      <>
        <div className={styles.searchBox}>
          <Input
            value={searchText}
            onChange={(e) => changeSearchText(e.currentTarget.value)}
            prefix={<SearchOutlined />}
            placeholder="Поиск"
            style={{ width: "100%" }}
            allowClear
          />
        </div>
        <div className={styles.tableBox} ref={refBox}>
          <Table<MfErrorRow>
            rowKey="id"
            columns={columns}
            dataSource={store.visibleErrors}
            pagination={false}
            size="small"
            scroll={{ y: tblHeight }}
            locale={{
              emptyText: (
                <Empty
                  description="Нет совпадений"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        </div>
      </>
    );
  },
);

const searchCounter: DebounceCounter = {};
